package modysy.model

import modysy.datastructures.Vector2

/** Models a rigid body.
  *
  * Notice that if material or geometry are changed, a call to recalculateMassAndInertia is required to
  * update mass and inertia values!
  *
  * @param pose     The transformation from the world origin to the center of mass of the object.
  * @param velocity The linear and angular velocity of the object.
  * @param material The material of the body, used to calculate its mass and inertia and to determine its
  *                 collision behaviour.
  * @param geometry The geometric shape of the object, used to calculate its mass and inertia and to find collisions
  *                 with other geometric shapes.
  */
class RigidBody(
    var pose: Transform,
    var velocity: Velocity,
    var material: Material,
    var geometry: Geometry
) {
  private var _mass: Double = 0
  private var _inverseMass: Double = 0
  private var _inertia: Double = 0
  private var _inverseInertia: Double = 0

  recalculateMassAndInertia()

  /** The mass of the body computed by the last call to recalculateMassAndInertia. */
  def mass: Double = _mass

  /** The inverse mass of the body computed by the last call to recalculateMassAndInertia. */
  def inverseMass: Double = _inverseMass

  /** The moment of intertia of the body around its center of mass computed by the last call
    * to recalculateMassAndInertia. */
  def inertia: Double = _inertia

  /** The inverse of inertia of the body around its center of mass computed by the last call
    * to recalculateMassAndInertia. */
  def inverseInertia: Double = _inverseInertia

  /** Update the values for mass and intertia based on the current geometry and material. */
  def recalculateMassAndInertia(): Unit = {
    _mass = geometry.area * material.density
    _inverseMass = 1 / _mass

    _inertia = geometry.calculateIntertia(_mass)
    _inverseInertia = 1 / _inertia
  }

  def contains(point: Vector2): Boolean = {
    geometry contains (pose.translationPart.inverse * point)
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/model/RigidBody.scala